import type { CaptureCapabilities, DisplayMediaProvider } from './types';

export interface CaptureEnvironment {
  readonly secureContext: boolean;
  readonly mediaDevices: Partial<DisplayMediaProvider> | undefined;
}

export interface BrowserCaptureEnvironment {
  readonly secureContext: boolean;
  readonly mediaDevices: DisplayMediaProvider | undefined;
}

export function detectCaptureCapabilities(
  environment: CaptureEnvironment = browserCaptureEnvironment(),
): CaptureCapabilities {
  if (!environment.secureContext) {
    return {
      supported: false,
      nativePicker: false,
      sharedAudio: 'unsupported',
      reason: 'insecure-context',
    };
  }

  if (environment.mediaDevices === undefined) {
    return {
      supported: false,
      nativePicker: false,
      sharedAudio: 'unsupported',
      reason: 'media-devices-unavailable',
    };
  }

  if (typeof environment.mediaDevices.getDisplayMedia !== 'function') {
    return {
      supported: false,
      nativePicker: false,
      sharedAudio: 'unsupported',
      reason: 'display-capture-unavailable',
    };
  }

  const supportedSettings = readSupportedConstraints(environment.mediaDevices);
  return {
    supported: true,
    nativePicker: true,
    sharedAudio: 'capture-dependent',
    settings: {
      displaySurface: supportedSettings.displaySurface === true,
      width: supportedSettings.width === true,
      height: supportedSettings.height === true,
      frameRate: supportedSettings.frameRate === true,
    },
  };
}

export function browserCaptureEnvironment(): BrowserCaptureEnvironment {
  return {
    secureContext: globalThis.isSecureContext === true,
    mediaDevices: readBrowserMediaDevices(),
  };
}

function readBrowserMediaDevices(): DisplayMediaProvider | undefined {
  try {
    return typeof navigator === 'undefined' ? undefined : navigator.mediaDevices;
  } catch {
    return undefined;
  }
}

function readSupportedConstraints(
  provider: Partial<DisplayMediaProvider>,
): MediaTrackSupportedConstraints {
  try {
    return provider.getSupportedConstraints?.() ?? {};
  } catch {
    return {};
  }
}
